import type { paths } from '@luxuryescapes/contract-svc-lux-loyalty'
import isLoyaltyCalculationRequestWithBonus from 'loyalty/lib/pointsCalculation/isLoyaltyCalculationRequestWithBonus'

type Payload = paths['/api/lux-loyalty/points/calculate-earn']['post']['requestBody']['content']['application/json']
type Response = paths['/api/lux-loyalty/points/calculate-earn']['post']['responses']['200']['content']['application/json']

export interface EarnPointsCalculationOptions {
  customerId: string
  currency: string
  luxPlusMember: boolean
  /** Decides which payment methods count as low cost, see `getLoyaltyEarnRegion` */
  region: string
}

export function mapProductDetails(request: App.LoyaltyPointsEarnCalculationRequest): Payload['productDetails'] {
  if (isLoyaltyCalculationRequestWithBonus(request)) {
    return {
      loyaltyProductType: request.loyaltyProductType,
      offerId: request.offerId,
      packageId: request.packageId,
      earnKey: request.earnKey,
      ratePlanId: request.ratePlanId,
      promoKey: request.promoKey,
    }
  }

  return {
    loyaltyProductType: request.loyaltyProductType,
    earnKey: request.earnKey,
    ratePlanId: request.ratePlanId,
    promoKey: request.promoKey,
  }
}

function mapOptions(
  request: App.LoyaltyPointsEarnCalculationRequest,
  options: EarnPointsCalculationOptions,
): Pick<EarnPointsCalculationOptions, 'currency' | 'region' | 'luxPlusMember'> {
  return {
    currency: request.currency ?? options.currency,
    region: request.region ?? options.region,
    luxPlusMember: request.luxPlusMember ?? options.luxPlusMember,
  }
}

export function mapRequestToPayload(request: App.LoyaltyPointsEarnCalculationRequest, options: EarnPointsCalculationOptions): Payload {
  return {
    publicPrice: request.price,
    luxPlusPrice: request.memberPrice,
    ...mapOptions(request, options),
    mx: request.mx,
    customerId: options.customerId,
    paymentMethods: request.paymentMethods,
    orderId: request.orderId,
    bookingDate: request.bookingDate,
    productDetails: mapProductDetails(request),
  }
}

export function mapResponseToCalculation(request: App.LoyaltyPointsEarnCalculationRequest, result: Response['result'], options: EarnPointsCalculationOptions): App.LoyaltyPointsEarnCalculation {
  const basePoints = result.totalBasePoints
  const bonusPoints = result.totalBonusPoints
  const bonusStatusCredits = result.totalBonusStatusCredits
  const statusCredits = result.totalStatusCredits
  const appliedRuleIds = result.appliedCustomRuleIds

  return {
    request: {
      ...request,
      ...mapOptions(request, options),
    },
    points: basePoints + bonusPoints,
    basePoints,
    bonusPoints,
    bonusStatusCredits,
    statusCredits,
    appliedRuleIds,
    customRulesBreakdown: result.customRulesBreakdown ?? [],
    earnDecisionsBreakdown: result.earnDecisionsBreakdown ?? [],
    roundingEnabled: result.roundingEnabled,
    roundedPointsEarnRatio: result.totalPointsEarnRatio,
  }
}
